import React, { useState, useEffect, useCallback, useRef } from "react";
import { fetchTicket, ticketReplyByAdmin } from "slices/Ticket/thunk";
import { ticketSuccessMsg, ticketErrorMsg } from "slices/Ticket/reducer";
import { useDispatch, useSelector } from "react-redux";
import {
    Row,
    Col,
    Card,
} from "reactstrap";

import { createSelector } from "reselect";
import BreadCrumb from "Components/Common/BreadCrumb";
import { handleValidDate, handleValidTime } from "helpers/common_helper";
import Loader from "Components/Common/Loader";
import { showErrorToast, showSuccessToast } from "helpers/toast_helper";
// import { uploadToSpaces } from "helpers/spaceupload";

interface TicketDetailPageProps {
    id: string;
    onBack: () => void;
    singleTicketData: any;
    loading: boolean;
}

const TicketDetailPage: React.FC<TicketDetailPageProps> = ({ id, onBack, singleTicketData, loading }) => {
    const [reply, setReply] = useState("");
    const [ticketData, setTicketData] = useState<any>([]);
    const dispatch: any = useDispatch();
    const selectLayoutState = (state: any) => state;
    const [fileUrl, setFileUrl] = useState<string | null>(null);
    const [fileName, setFileName] = useState<string | null>(null);
    const [uploading, setUploading] = useState<boolean>(false);

    const ticketSelector = createSelector(selectLayoutState, (state) => ({
        successMsg: state.Ticket.ticketSuccessMsg,
        errorMsg: state.Ticket.deleteErrorMsg,
        userChangePasswordSuccess: state.ChangePassword.changePwdSuccessMsg,
        userChangePasswordError: state.ChangePassword.changePasswordError,
    }))
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const handleFileClick = () => {
        fileInputRef.current?.click(); 
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Check file size (e.g., max 5MB)
            const maxSize = 5 * 1024 * 1024; // 5MB in bytes
            if (file.size > maxSize) {
                showErrorToast("File size should be less than 5MB");
                return;
            }

            // Check if it's an image or PDF
            const isImage = file.type.startsWith('image/');
            const isPDF = file.type === 'application/pdf';
            
            if (isImage || isPDF) {
                try {
                    setUploading(true);
                    const reader = new FileReader();
                    
                    reader.onloadend = () => {
                        const base64String = reader.result as string;
                        setFileUrl(base64String);
                        setFileName(file.name);
                        setUploading(false);
                        showSuccessToast("File selected successfully!");
                    };
                    
                    reader.onerror = () => {
                        setUploading(false);
                        showErrorToast("Error reading file!");
                    };
                    
                    reader.readAsDataURL(file);
                } catch (error) {
                    console.error("Error processing file:", error);
                    showErrorToast("Error processing file!");
                    setUploading(false);
                }
            } else {
                showErrorToast("Please select an image or PDF file");
            }
        }
        // Reset input value to allow selecting the same file again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const { successMsg, errorMsg, userChangePasswordError, userChangePasswordSuccess } = useSelector(ticketSelector)

    useEffect(() => {
        if (singleTicketData) {
            setTicketData(singleTicketData);
        }
    }, [singleTicketData]);

    const handleReply = () => {
        if (ticketData?.status === "Closed") {
            showErrorToast("Ticket already closed");
            return
        }
        const data: {
            ticket_id: number;
            activity_description: string;
            status: string;
            attachment_url?: string | null;
        } = {
            ticket_id: ticketData?.id as number || 0, // or handle error
            activity_description: reply || "",
            status: "Picked",
            attachment_url: fileUrl  || null
        }
        dispatch(ticketReplyByAdmin(data))
        setReply("");
        setFileUrl(null);
        setFileName(null);
    }

    const handleBack = () => {
        onBack();
    };

    const handleResolveTicket = () => {
        // Compute the next status first
        const newStatus = ticketData?.status === "Closed" ? "Open" : "Closed";

        // Update the state for UI


        const data: {
            ticket_id: number;
            activity_description: string;
            status: string;
            attachment_url?: string | null;
        } = {
            ticket_id: ticketData?.id as number || 0, // or handle error
            activity_description: newStatus === "Closed" ? "Ticket Closed by Admin" : "Ticket Reopened by Admin",
            status: newStatus,
            // status: ticketData?.status !== "Closed" && ticketData?.status === "Open" ? "Open" : "Closed",
            attachment_url: null
        }
        dispatch(ticketReplyByAdmin(data))
    }


    useEffect(() => {

        if (userChangePasswordSuccess) {
            showSuccessToast(userChangePasswordSuccess);
        }
        if (userChangePasswordError) {
            showErrorToast(userChangePasswordError);
        }
        if (successMsg?.id) {
            showSuccessToast(successMsg.message);
            dispatch(
                ticketSuccessMsg({
                    message: "",
                    id: "",
                })
            );
        }
        if (errorMsg?.id) {
            showErrorToast(errorMsg.message);
            dispatch(
                ticketErrorMsg({
                    message: "",
                    id: "",
                })
            );
        }


    }, [userChangePasswordSuccess, userChangePasswordError, successMsg?.id, errorMsg?.id]);

    return (
        <>
            {loading ? <Loader /> :
                <div className="d-flex flex-column ms-1" style={{ minHeight: '100vh' }}>

                    <BreadCrumb
                        title="Ticket Details"
                        pageTitle={["Ticket Report", "Ticket", "Details"]}
                    />
                    {/* Header Section */}
                    <Row className="align-items-center justify-content-between border-bottom pb-3 mb-3">
                        {/* Left side: Back to Tickets */}
                        <Col xs="auto">
                            <div
                                onClick={handleBack}
                                style={{ cursor: "pointer" }}
                                className="d-flex align-items-center gap-2"
                            >
                                <i className="ri-arrow-left-line"></i>
                                <span className="text-primary">Back to Tickets</span>
                            </div>
                        </Col>

                        {/* Right side: Buttons */}
                        <Col xs="auto" className="d-flex gap-2">

                            <button
                                className="btn btn-success rounded-3 px-3 py-1 bg-primary border-primary"

                                onClick={handleResolveTicket}

                            >
                                {ticketData?.status === "Closed" ? "Reopen Ticket" : "Mark as Resolved"}
                            </button>
                        </Col>
                    </Row>

                    <div className="container-fluid py-3" style={{ minHeight: "100vh" }}>
                        <Row className="g-4">
                            <Col lg={8}>
                                <Card className="shadow-sm border-0 rounded-4 p-4 mb-3 bg-light">
                                    <h5 className="fw-semibold mb-1 text-primary">
                                        #{ticketData["ticket_no "] ? ticketData["ticket_no "] : "N/A"}{" "}
                                        <span
                                            className="badge ms-2"
                                            style={{
                                                backgroundColor:
                                                    ticketData?.status === "New" || ticketData?.status === "Open"
                                                        ? "rgba(83, 116, 192, 0.15)" // soft blue
                                                        : ticketData?.status === "Closed"
                                                            ? "rgba(21, 158, 58, 0.15)" // soft green
                                                            : ticketData?.status === "Picked" || ticketData?.status === "Deleted"
                                                                ? "rgba(166, 95, 0, 0.15)" // soft brown-orange
                                                                : "",
                                                color:
                                                    ticketData?.status === "New" || ticketData?.status === "Open"
                                                        ? "#5374C0"
                                                        : ticketData?.status === "Closed"
                                                            ? "#159E3A"
                                                            : ticketData?.status === "Picked" || ticketData?.status === "Deleted"
                                                                ? "#A65F00"
                                                                : "",
                                                borderRadius: "20px",
                                                padding: "4px 20px",
                                                font: "12px",
                                            }}
                                        >
                                            {ticketData?.status?.trim()
                                                ? ticketData.status.trim().toLowerCase() === "closed"
                                                    ? "Solved"
                                                    : ticketData.status.trim()
                                                : "N/A"}
                                        </span>

                                    </h5>
                                    <h4 className="fw-bold mb-2">
                                        {ticketData["ticket_subject"]
                                            ? ticketData["ticket_subject"].charAt(0).toUpperCase() + ticketData["ticket_subject"].slice(1)
                                            : "N/A"}
                                    </h4>

                                    <div className="text-muted small mb-3">
                                        <i className="ri-time-line me-1"></i>Created: {" "}
                                        {ticketData?.createdon ? (
                                            <>
                                                {handleValidDate(ticketData.createdon)},{" "}
                                                <small className="text-muted">
                                                    {handleValidTime(ticketData.createdon)}
                                                </small> {" "}
                                            </>
                                        ) : (
                                            <>N/A</>
                                        )}
                                        &nbsp;&nbsp;&nbsp;
                                        <i
                                            className="ri-price-tag-3-line"
                                            style={{ display: "inline-block", transform: "scaleX(-1)" }}
                                        >   </i>

                                        Account
                                    </div>

                                    <h6 className="fw-semibold mt-3 mb-2">Description</h6>
                                    <p className="text-muted" style={{ whiteSpace: "pre-line" }}>
                                        {ticketData?.ticket_description?.trim()
                                            ? ticketData.ticket_description
                                                .trim()
                                                .charAt(0)
                                                .toUpperCase() +
                                            ticketData.ticket_description.trim().slice(1)
                                            : "N/A"}
                                    </p>
                                    
                                </Card>

                                {/* Add Reply Container - Only show if ticket is not Closed or Solved */}
                                {ticketData?.status !== "Closed" && ticketData?.status?.trim().toLowerCase() !== "solved" && (
                                    <Card className="shadow-sm border-0 rounded-4 p-4 bg-light">
                                        <h6 className="fw-semibold mb-3 ms-1">Add Reply</h6>
                                        <textarea
                                            className="form-control rounded-3 mb-3 border-1"
                                            rows={4}
                                            placeholder="Type your reply here..."
                                            value={reply}
                                            onChange={(e) => setReply(e.target.value)}
                                        ></textarea>

                                         <div className="d-flex justify-content-end gap-2">

                     


            <input
        type="file"
        accept="image/*,.pdf" 
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <div className="d-flex align-items-center gap-2">
        <button
          className="btn btn-outline-primary rounded-3 px-4"
          onClick={handleFileClick}
          disabled={uploading}
        >
          <i className="ri-attachment-2 me-2"></i>
          {uploading ? "Processing..." : "Attach Files"}
        </button>

        {fileName && (
          <div className="d-flex align-items-center gap-2">
            <span className="text-primary small" style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              <i className={fileName.toLowerCase().endsWith('.pdf') ? "ri-file-pdf-line me-1" : "ri-file-image-line me-1"}></i>
              {fileName}
            </span>
            <button
              className="btn btn-sm btn-link text-danger p-0"
              onClick={() => {
                setFileUrl(null);
                setFileName(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
              style={{ fontSize: "0.8rem" }}
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        )}
      </div>

          {/* ✅ Send Button */}
          <button
            className="btn btn-primary rounded-3 px-4"
            onClick={handleReply}
            disabled={!reply.trim()}
            style={{
              opacity: !reply.trim() ? 0.6 : 1,
              cursor: !reply.trim() ? "not-allowed" : "pointer",
            }}
          >
            <i className="ri-send-plane-line me-2"></i> Send Reply
          </button>

        </div>
                                    </Card>
                                )}

                            </Col>

                            {/* RIGHT COLUMN */}
                            <Col lg={4}>
                                {/* Customer Information */}
                                <Card className="shadow-sm border-0 rounded-4 p-4 mb-3 bg-light">
                                    <h6 className="fw-semibold mb-3">Customer Information</h6>
                                    <div className="d-flex align-items-center mb-3">
                                        {/* <img
                                            src="https://via.placeholder.com/50"
                                            alt="User"
                                            className="rounded-circle me-3"
                                        /> */}
                                        <div
                                            data-no-highlight
                                            className="avatar-title border rounded-circle bg-light text-primary d-flex justify-content-center align-items-center"
                                            style={{
                                                width: "40px",
                                                height: "40px",
                                                marginRight: "10px",
                                                fontSize: "12px",
                                            }}
                                        >
                                            {ticketData.createdby ? ticketData.createdby.charAt(0).toUpperCase() : "N/A"}
                                        </div>
                                        <div>
                                            <h6 className="mb-0">{ticketData?.createdby
                                                ? ticketData.createdby.charAt(0).toUpperCase() + ticketData.createdby.slice(1)
                                                : "N/A"
                                            }</h6>
                                            {/* <small className="text-muted">john.anderson@email.com</small> */}
                                        </div>
                                    </div>
                                    <div className="d-flex justify-content-between text-muted small">
                                        {/* TO Be implemented after api */}
                                        {/* <div>
                                            <div className="fw-semibold">TOTAL TICKETS</div>
                                            <div>8 Tickets</div>
                                        </div>
                                        <div>
                                            <div className="fw-semibold">CUSTOMER SINCE</div>
                                            <div>March 2020</div>
                                        </div>
                                        <div>
                                            <div className="fw-semibold">ACCOUNT TYPE</div>
                                            <div>Premium</div>
                                        </div> */}
                                    </div>
                                </Card>

                                {/* Ticket Overview */}
                                <Card className="shadow-sm border-0 rounded-4 p-4 mb-3 bg-light">
                                    <h6 className="fw-semibold mb-3">Ticket Overview</h6>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="text-muted small">STATUS</div>
                                            <span className="badge"
                                             style={{
                                                backgroundColor:
                                                    ticketData?.status === "New" || ticketData?.status === "Open"
                                                        ? "rgba(83, 116, 192, 0.15)" // soft blue
                                                        : ticketData?.status === "Closed"
                                                            ? "rgba(21, 158, 58, 0.15)" // soft green
                                                            : ticketData?.status === "Picked" || ticketData?.status === "Deleted"
                                                                ? "rgba(166, 95, 0, 0.15)" // soft brown-orange
                                                                : "",
                                                color:
                                                    ticketData?.status === "New" || ticketData?.status === "Open"
                                                        ? "#5374C0"
                                                        : ticketData?.status === "Closed"
                                                            ? "#159E3A"
                                                            : ticketData?.status === "Picked" || ticketData?.status === "Deleted"
                                                                ? "#A65F00"
                                                                : "",
                                                borderRadius: "20px",
                                                padding: "4px 20px",
                                                font: "12px",
                                            }}>{ticketData?.status?.trim() ? ticketData.status.trim().toLowerCase() === "closed" ? "Solved" : ticketData.status.trim() : "N/A"}
                                            </span>
                                        </div>
                                        <div>
                                            <div className="text-muted small">LAST UPDATED</div>
                                            <div>       {ticketData?.updatedon ? (
                                                <>
                                                    {handleValidDate(ticketData.updatedon)},{" "}
                                                    <small className="text-muted">
                                                        {handleValidTime(ticketData.updatedon)}
                                                    </small> {" "}
                                                </>
                                            ) : (
                                                <>N/A</>
                                            )}</div>
                                        </div>
                                    </div>
                                </Card>


                                <Card className="shadow-sm border-0 rounded-4 p-4 bg-light">
                                    <h6 className="fw-semibold mb-4">Activity Timeline</h6>
                                   <div style={{
                                    maxHeight:
                                        ticketData.activities?.length > 3 ? "300px" : "auto", // scroll only after 3
                                    overflowY:
                                        ticketData.activities?.length > 3 ? "auto" : "visible", // enable scroll only when needed
                                    paddingRight: ticketData.activities?.length > 3 ? "6px" : "0",
                                    }}>
                                    <ul className="list-unstyled mb-0" style={{ position: "relative", paddingLeft: "15px" }}>
                                        {ticketData.activities?.length > 0 ? (
                                            ticketData.activities.map((activity: { createdby: string; id: React.Key | null | undefined; status: string; createdon: any; feedback:string; activity_description: any; attachments: any[]; }, index: number) => {
                                                const initials = activity.createdby
                                                    ? activity.createdby
                                                        .split(" ")
                                                        .map((n) => n[0])
                                                        .join("")
                                                        .substring(0, 2)
                                                        .toUpperCase()
                                                    : "U";

                                                const isLast = index === ticketData.activities.length - 1;

                                                return (
                                                    <li
                                                        key={activity.id}
                                                        className="pb-4"
                                                        style={{
                                                            position: "relative",
                                                            marginBottom: isLast ? "0" : "10px",
                                                        }}
                                                    >
                                                        {/* Vertical timeline line */}
                                                        {!isLast && (
                                                            <div
                                                                style={{
                                                                    position: "absolute",
                                                                    left: "14px",
                                                                    top: "35px",
                                                                    bottom: "0",
                                                                    width: "2px",
                                                                    backgroundColor: "#e5e7eb",
                                                                }}
                                                            ></div>
                                                        )}    
                                                            {activity.feedback && (
                                                                <div className="fw-feedback mb-2">
                                                                <strong>Feedback:</strong> {activity.feedback}
                                                                </div>
                                                            )}                                                
                                                        {/* Avatar, name, date */}
                                                        <div className="d-flex align-items-center mb-1">
                                                            <div
                                                                className="rounded-circle text-white fw-bold d-flex align-items-center justify-content-center me-2"
                                                                style={{
                                                                    width: "30px",
                                                                    height: "30px",
                                                                    backgroundColor:
                                                                        activity.createdby?.toLowerCase().includes("system") ||
                                                                            activity.status === "System"
                                                                            ? "#10B981" // green for system
                                                                            : "#F59E0B", // orange for users
                                                                    fontSize: "12px",
                                                                }}
                                                            >
                                                                {initials}
                                                            </div>
                                                            <span className="fw-semibold" style={{ fontSize: "0.9rem" }}>
                                                                {activity.createdby || "Unknown User"}
                                                            </span>
                                                            <small className="text-muted ms-2" style={{ fontSize: "0.8rem" }}>
                                                                {handleValidDate(activity.createdon)}
                                                            </small>
                                                        </div>

                                                        {/* Description */}
                                                        <div
                                                            className="text-muted"
                                                            style={{
                                                                marginLeft: "36px",
                                                                fontSize: "0.9rem",
                                                                whiteSpace: "pre-line",
                                                                lineHeight: "1.5",
                                                            }}
                                                        >
                                                            {activity.activity_description || (
                                                                <span className="fst-italic text-muted">No description provided.</span>
                                                            )}
                                                        </div>

                                                        {/* Attachments */}
                                                        {activity.attachments?.length > 0 && (
                                                            <div style={{ marginLeft: "36px", marginTop: "8px" }}>
                                                                <div className="fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>
                                                                    Attachments
                                                                </div>
                                                                <div className="d-flex flex-wrap gap-2">
                                                                    {activity.attachments.map((file, idx) => (
                                                                        <a
                                                                            key={idx}
                                                                            href={file.fileurl}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                            className="d-inline-flex align-items-center"
                                                                            style={{
                                                                                textDecoration: "none",
                                                                                backgroundColor: "#f3f4f6",
                                                                                color: "#111827",
                                                                                borderRadius: "8px",
                                                                                padding: "4px 8px",
                                                                                fontSize: "0.8rem",
                                                                            }}
                                                                        >
                                                                            <i className="ri-attachment-2 me-1" style={{ fontSize: "0.9rem" }}></i>
                                                                            {file.filename.length > 20
                                                                                ? file.filename.slice(0, 20) + "..."
                                                                                : file.filename}
                                                                        </a>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </li>
                                                );
                                            })
                                        ) : (
                                            <div className="text-center text-muted py-3">No activity found.</div>
                                        )}
                                    </ul>
                                    </div>
                                </Card>

                            </Col>
                        </Row>


                    </div>

                </div>}
        </>
    );
};

export default TicketDetailPage;
